import { EyeIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import type { DataTableColumn } from '@odo/shared-ui';
import { PatientLink } from '../PatientLink';
import { RowMenu, type RowMenuItem } from '../RowMenu';
import { StatusBadge } from '../StatusBadge';
import { isInvoiceVoidable, type InvoiceListItem } from '../../../services/billing';
import { formatMoneyDollars } from '../../../utils/money';

export type InvoicesSortBy = 'due' | 'amount' | 'date' | 'patient';

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

// Past due while money is still expected; paid/void/error invoices are not "late".
function isOverdue(invoice: InvoiceListItem): boolean {
    const awaitingPayment = ['pending', 'created', 'open', 'partial'].includes(invoice.status);
    return awaitingPayment && new Date(invoice.dueDate).getTime() < Date.now();
}

function menuItemsFor(
    invoice: InvoiceListItem,
    canEdit: boolean,
    onOpenCharge: (chargeId: string) => void,
    onVoid: (invoice: InvoiceListItem) => void
): RowMenuItem[] {
    const items: RowMenuItem[] = [
        {
            label: 'View charge',
            icon: <EyeIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onOpenCharge(invoice.chargeId)
        }
    ];
    if (canEdit && isInvoiceVoidable(invoice.status)) {
        items.push({
            label: 'Void invoice',
            icon: <NoSymbolIcon className="h-4 w-4" />,
            tone: 'danger',
            onClick: () => onVoid(invoice)
        });
    }
    return items;
}

interface BuildInvoiceColumnsArgs {
    showPracticeColumn: boolean;
    canEdit: boolean;
    onOpenCharge: (chargeId: string) => void;
    onVoid: (invoice: InvoiceListItem) => void;
}

/**
 * The two colgroups this table replaces used different percentages for their
 * shared columns depending on whether the Practice column is shown, so every
 * width below is a ternary on the same `showPracticeColumn` flag.
 */
export function buildInvoiceColumns({
    showPracticeColumn,
    canEdit,
    onOpenCharge,
    onVoid
}: BuildInvoiceColumnsArgs): DataTableColumn<InvoiceListItem, InvoicesSortBy>[] {
    return [
        {
            key: 'invoiceNumber',
            label: 'Invoice',
            width: showPracticeColumn ? '11%' : '14%',
            cellClassName: 'pl-6 pr-3 font-mono text-[12px] text-gray-700 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3 pl-6',
            render: (invoice) => invoice.invoiceNumber
        },
        {
            key: 'practice',
            label: 'Practice',
            width: '16%',
            visible: showPracticeColumn,
            truncate: true,
            cellClassName: 'px-3 text-sm',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (invoice) => invoice.practiceName,
            // Same name-over-doctor pairing as the Charges practice column.
            render: (invoice) => (
                <>
                    <span
                        className="block text-sm font-medium text-gray-800 truncate"
                        title={invoice.practiceName}
                    >
                        {invoice.practiceName}
                    </span>
                    {invoice.practiceDoctorName && (
                        <span
                            className="block text-xs text-gray-400 truncate"
                            title={`Dr. ${invoice.practiceDoctorName}`}
                        >
                            Dr. {invoice.practiceDoctorName}
                        </span>
                    )}
                </>
            )
        },
        {
            key: 'patient',
            label: 'Patient',
            width: showPracticeColumn ? '14%' : '17%',
            sortable: true,
            sortKey: 'patient',
            truncate: true,
            cellClassName: 'px-3 text-sm',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (invoice) => invoice.patientName ?? undefined,
            render: (invoice) => (
                <PatientLink patientId={invoice.patientId} patientName={invoice.patientName}>
                    {invoice.patientName ? (
                        <span className="text-gray-700">{invoice.patientName}</span>
                    ) : (
                        <span className="text-gray-300">-</span>
                    )}
                </PatientLink>
            )
        },
        {
            key: 'installments',
            label: 'Inst.',
            width: showPracticeColumn ? '7%' : '9%',
            cellClassName: 'px-3 text-sm text-gray-500 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (invoice) => `${invoice.installmentIndex + 1} of ${invoice.installments}`
        },
        {
            key: 'amount',
            label: 'Amount',
            width: showPracticeColumn ? '13%' : '16%',
            sortable: true,
            sortKey: 'amount',
            align: 'right',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (invoice) => (
                <span className="text-sm font-semibold text-gray-900 tabular-nums">
                    {formatMoneyDollars(invoice.amount)}
                </span>
            )
        },
        {
            key: 'created',
            label: 'Created',
            width: showPracticeColumn ? '11%' : '12%',
            sortable: true,
            sortKey: 'date',
            cellClassName: 'px-3 text-sm text-gray-500 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (invoice) => fmtDate(invoice.createdAt)
        },
        {
            key: 'due',
            label: 'Due',
            width: showPracticeColumn ? '12%' : '14%',
            sortable: true,
            sortKey: 'due',
            cellClassName: 'px-3 text-sm whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (invoice) => (
                <span className={isOverdue(invoice) ? 'text-red-600 font-medium' : 'text-gray-500'}>
                    {fmtDate(invoice.dueDate)}
                </span>
            )
        },
        {
            key: 'status',
            label: 'Status',
            width: showPracticeColumn ? '11%' : '12%',
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap px-3',
            render: (invoice) => <StatusBadge status={invoice.status} />
        },
        {
            key: 'menu',
            label: '',
            width: showPracticeColumn ? '5%' : '6%',
            truncate: false,
            stopClickPropagation: true,
            cellClassName: 'px-3 w-12',
            headerClassName: 'whitespace-nowrap px-3',
            render: (invoice) => {
                const items = menuItemsFor(invoice, canEdit, onOpenCharge, onVoid);
                return items.length > 0 ? <RowMenu items={items} /> : null;
            }
        }
    ];
}
